import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { examples, extraQuotes, sampleCareNote, homeQuote } from '../data/examples';
import SampleNote from '../components/SampleNote';
import FinalCTA from '../components/home/FinalCTA';
import '../styles/home.css';

const Examples = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Who We Help | ManagedSite.net';
    const description =
      'How ManagedSite.net takes on website, email, and hosting work for small businesses and agencies. Request a Digital Review.';
    document.title = title;
    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };
    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  return (
    <div className="home-page w-full">
      <section className="relative overflow-hidden bg-[#174C3C] pt-12 pb-12 text-white lg:pt-20 lg:pb-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_.9fr] lg:gap-16">
            <div className="max-w-3xl">
              <div className="mb-6 text-xs font-bold uppercase tracking-[0.2em] text-[#D7B77A]">
                Who we help
              </div>
              <h1 className="text-5xl leading-[1.03] text-white sm:text-6xl lg:text-7xl">
                The kind of work we{' '}
                <span className="text-[#D7B77A]">take on.</span>
              </h1>
              <p className="mt-8 max-w-2xl text-xl leading-relaxed text-white/75">
                First names only. The situations are the ones we see most: an
                agency that needs a technical bench, a business whose setup is
                split across vendors, or a move that cannot wait until Monday.
              </p>
              <div className="mt-10">
                <Link to="/digital-review" className="home-button-on-dark bg-white">
                  Request a Digital Review
                </Link>
              </div>
            </div>

            <blockquote className="home-paper relative border border-[#3B7C6D]/20 p-7 text-[#17211E] sm:p-10">
              <div className="absolute -right-3 -top-3 h-8 w-8 border-r-2 border-t-2 border-[#D7B77A]" />
              <p className="home-serif text-2xl leading-snug text-[#174C3C]">
                “{homeQuote.text}”
              </p>
              <footer className="mt-8 text-sm font-bold uppercase tracking-[0.18em] text-[#17211E]/45">
                {homeQuote.attribution}
              </footer>
            </blockquote>
          </div>
        </div>
      </section>

      <section className="border-b border-[#17211E]/10 bg-white pt-12 pb-16 lg:pt-16 lg:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-2">
            {examples.map((example, index) => {
              const isGreen = index === 1 || index === 2;

              return (
              <article
                key={example.id}
                className={`p-8 sm:p-10 ${
                  isGreen
                    ? 'border border-[#174C3C] bg-[#174C3C] text-white'
                    : 'border border-[#17211E]/15 bg-[#FAF8F3]'
                }`}
              >
                <div className="flex items-baseline justify-between gap-4">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#D7B77A]">
                    {example.label}
                  </div>
                  <div
                    className={`text-xs font-bold uppercase tracking-[0.18em] ${
                      isGreen ? 'text-white/45' : 'text-[#17211E]/45'
                    }`}
                  >
                    {example.kind}
                  </div>
                </div>
                <p
                  className={`mt-8 text-lg leading-relaxed ${
                    isGreen ? 'text-white/75' : 'text-[#17211E]/75'
                  }`}
                >
                  {example.situation}
                </p>
                <div
                  className={`mt-8 border-t pt-6 ${
                    isGreen ? 'border-white/15' : 'border-[#17211E]/15'
                  }`}
                >
                  <div
                    className={`text-xs font-bold uppercase tracking-[0.18em] ${
                      isGreen ? 'text-white/45' : 'text-[#17211E]/45'
                    }`}
                  >
                    What we did
                  </div>
                  <p
                    className={`mt-3 text-lg leading-relaxed ${
                      isGreen ? 'text-white' : 'text-[#17211E]'
                    }`}
                  >
                    {example.whatWeDo}
                  </p>
                </div>
              </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[#EAF1EC] py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-2xl">
            <div className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-[#3B7C6D]">
              How people describe the work
            </div>
            <h2 className="text-4xl leading-tight sm:text-5xl">
              From clients, in their words.
            </h2>
          </div>
          <div className="max-w-3xl">
            {extraQuotes.map((quote) => (
              <blockquote
                key={quote.id}
                className="border-l-2 border-[#D7B77A] bg-[#FAF8F3] p-8 sm:p-10"
              >
                <p className="home-serif text-2xl leading-snug text-[#174C3C]">
                  “{quote.text}”
                </p>
                <footer className="mt-8 text-sm font-bold uppercase tracking-[0.18em] text-[#17211E]/45">
                  {quote.attribution}
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FAF8F3] py-24">
        <div className="mx-auto grid max-w-7xl items-start gap-16 px-4 sm:px-6 lg:grid-cols-[1fr_1.05fr] lg:px-8">
          <div>
            <div className="mb-5 text-xs font-bold uppercase tracking-[0.2em] text-[#3B7C6D]">
              What a month looks like
            </div>
            <h2 className="text-4xl leading-tight sm:text-5xl">
              A care note, not a mystery.
            </h2>
            <p className="mt-7 max-w-xl text-xl leading-relaxed text-[#17211E]/70">
              Eligible plans include a written note: what was reviewed, what
              changed, and what needs a decision. You are not left guessing
              whether anyone looked.
            </p>
            <Link to="/digital-review" className="home-link mt-8 inline-block">
              Request a Digital Review
            </Link>
          </div>

          <SampleNote note={sampleCareNote} />
        </div>
      </section>

      <FinalCTA />
    </div>
  );
};

export default Examples;
